import { useState } from 'react';
import SubtaskList, { SubtaskProgress } from './SubtaskList';
import { useSubtasks } from '../hooks/useSubtasks';
import { describeRecurrence, formatNextDue } from '../utils/recurrence';

const PRI_COLORS = { high: '#DC2626', medium: '#D97706', low: '#16A34A' };

function EditIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
      <path d="M10 11v6"/><path d="M14 11v6"/>
      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
    </svg>
  );
}
function ChevronIcon({ open }) {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  );
}
function RepeatIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="17 1 21 5 17 9"/>
      <path d="M3 11V9a4 4 0 014-4h14"/>
      <polyline points="7 23 3 19 7 15"/>
      <path d="M21 13v2a4 4 0 01-4 4H3"/>
    </svg>
  );
}
function DragHandle() {
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" fill="none" style={{ display:'block' }}>
      <circle cx="3" cy="3"  r="1.4" fill="currentColor"/>
      <circle cx="7" cy="3"  r="1.4" fill="currentColor"/>
      <circle cx="3" cy="7"  r="1.4" fill="currentColor"/>
      <circle cx="7" cy="7"  r="1.4" fill="currentColor"/>
      <circle cx="3" cy="11" r="1.4" fill="currentColor"/>
      <circle cx="7" cy="11" r="1.4" fill="currentColor"/>
    </svg>
  );
}

// rowProps  → spread on root div  (onDragOver, onDrop — NO draggable)
// handleProps → spread on handle icon (draggable:true, onDragStart, onDragEnd)
export default function TaskItem({ task, onToggle, onDelete, onEdit, rowProps, handleProps }) {
  const [removing,      setRemoving]      = useState(false);
  const [hovering,      setHovering]      = useState(false);
  const [checking,      setChecking]      = useState(false);
  const [expanded,      setExpanded]      = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);

  const {
    subtasks, progress, loading: subtaskLoading,
    fetchFromApi, addSubtask, toggleSubtask, updateSubtask, removeSubtask,
  } = useSubtasks(task.id, task.subtasks || []);

  // Local date string (avoids UTC timezone shift)
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  })();

  const isDueToday  = task.due === 'today' || (task.due_date && task.due_date.slice(0,10) === todayStr);
  const canComplete = isDueToday;
  const isRecurring = !!(task.recurrence_type && task.recurrence_type !== 'none');
  const priColor    = PRI_COLORS[task.priority] || PRI_COLORS.medium;

  // Parse recurrence config safely
  const recurConfig = (() => {
    try { return typeof task.recurrence_config === 'string' ? JSON.parse(task.recurrence_config) : (task.recurrence_config || null); }
    catch { return null; }
  })();
  const recurLabel   = isRecurring ? describeRecurrence(task.recurrence_type, recurConfig) : null;

  // next_due_date from server — show as "Next: Mar 17"
  const nextDueDate  = task.next_due_date
    ? (typeof task.next_due_date === 'string' ? task.next_due_date : task.next_due_date)
    : null;
  const nextDueLabel = isRecurring && nextDueDate ? formatNextDue(nextDueDate) : null;

  const handleDelete = () => {
    setRemoving(true);
    setTimeout(() => onDelete(task.id), 260);
  };

  const handleToggle = async (e) => {
    // Stop event from bubbling to drag handlers on root div
    e.stopPropagation();
    if (!canComplete) return;
    if (task._completing) return; // already in-flight
    setChecking(true);
    await onToggle(task.id);
    setChecking(false);
    if (isRecurring) {
      setJustCompleted(true);
      setTimeout(() => setJustCompleted(false), 2400);
    }
  };

  const handleExpand = (e) => {
    e.stopPropagation();
    const next = !expanded;
    setExpanded(next);
    if (next) fetchFromApi();
  };

  const isDraggable = !!(rowProps && handleProps);

  return (
    <div
      {...(rowProps || {})}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        borderBottom: '1px solid var(--divider)',
        opacity:   removing ? 0 : 1,
        transform: removing ? 'translateY(-4px)' : 'none',
        transition: 'opacity 0.25s, transform 0.25s',
        background: hovering ? 'var(--hover)' : 'transparent',
        // cursor is grab only when actually over the drag handle (handled by handle CSS)
      }}
    >
      <div style={{ display:'flex', alignItems:'flex-start', gap: 10, padding:'10px 12px 10px 10px' }}>

        {/* ── Drag handle — ONLY this element has draggable=true ── */}
        {isDraggable && (
          <div
            {...handleProps}
            title="Drag to reorder"
            style={{
              opacity: hovering ? 0.4 : 0,
              transition: 'opacity 0.15s',
              marginTop: 3,
              flexShrink: 0,
              cursor: 'grab',
              color: 'var(--text3)',
              padding: '2px 1px',
              // Prevent text selection during drag
              userSelect: 'none',
              WebkitUserSelect: 'none',
            }}
          >
            <DragHandle />
          </div>
        )}

        {/* ── Checkbox — NOT draggable, click always works ── */}
        <div
          onClick={handleToggle}
          title={
            !canComplete       ? 'Only tasks due today can be completed'
            : isRecurring      ? 'Mark done — resets for next cycle'
            : task._completing ? 'Saving…'
            : ''
          }
          style={{
            width: 18, height: 18, borderRadius: '50%',
            flexShrink: 0, marginTop: 2,
            cursor: canComplete && !task._completing ? 'pointer' : 'not-allowed',
            border: !canComplete
              ? '1.5px dashed var(--border)'
              : task._completing
                ? `1.5px solid ${priColor}`
                : task.done ? 'none' : `1.5px solid ${priColor}`,
            background: !canComplete
              ? 'transparent'
              : task._completing ? 'transparent'
              : task.done ? priColor : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.18s',
            opacity: !canComplete ? 0.35 : checking ? 0.6 : 1,
            animation: task._completing ? 'taskCheckSpin 0.6s linear infinite' : 'none',
          }}
        >
          {task.done && !task._completing && canComplete && (
            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
              <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          <style>{`@keyframes taskCheckSpin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
        </div>

        {/* ── Content ── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 400, lineHeight: 1.45,
            color: task.done ? 'var(--text3)' : 'var(--text)',
            textDecoration: task.done ? 'line-through' : 'none',
            transition: 'color 0.15s',
            wordBreak: 'break-word',
          }}>
            {task.title}
          </div>

          {/* Recurring just-completed flash */}
          {justCompleted && (
            <div style={{
              marginTop: 4, padding: '3px 8px', borderRadius: 6, display: 'inline-flex',
              alignItems: 'center', gap: 5,
              background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.25)',
              fontSize: 11, fontWeight: 600, color: '#16A34A',
            }}>
              🔁 Done! Resets for next cycle
            </div>
          )}

          {/* Meta row: category · date · recurrence · next due */}
          {(task.category || task.due_date || isRecurring) && (
            <div style={{ display:'flex', gap: 6, marginTop: 3, alignItems:'center', flexWrap:'wrap' }}>
              {task.category && (
                <span style={{ fontSize: 11, fontWeight: 500, color: task.category.color || '#888', display:'inline-flex', alignItems:'center', gap: 3 }}>
                  {task.category.icon && <span style={{ fontSize: 10 }}>{task.category.icon}</span>}
                  {task.category.name}
                </span>
              )}
              {task.due_date && !isRecurring && (
                <span style={{ fontSize: 11, color:'var(--text3)', fontWeight: 500 }}>
                  {new Date(task.due_date + 'T00:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' })}
                </span>
              )}
              {isRecurring && recurLabel && (
                <span style={{ display:'inline-flex', alignItems:'center', gap: 3, fontSize: 11, color:'var(--accent)', fontWeight: 500 }}>
                  <RepeatIcon />{recurLabel}
                </span>
              )}
              {/* Next due date — updated by server after completion */}
              {nextDueLabel && (
                <span style={{ fontSize: 11, color:'var(--text3)', fontWeight: 500 }}>
                  · Next: {nextDueLabel}
                </span>
              )}
            </div>
          )}

          {!expanded && subtasks.length > 0 && (
            <SubtaskProgress done={progress.done} total={progress.total} />
          )}
        </div>

        {/* ── Actions ── */}
        <div style={{ display:'flex', alignItems:'center', gap: 2, flexShrink: 0 }}>
          <button onClick={handleExpand} title={expanded ? 'Hide subtasks' : subtasks.length > 0 ? 'Show subtasks' : 'Add subtasks'}
            style={{ background:'none', border:'none', cursor:'pointer', color: subtasks.length > 0 ? 'var(--text2)' : 'var(--text3)', padding:'4px 5px', borderRadius: 5, display:'flex', alignItems:'center', gap: 3, opacity:(subtasks.length>0||hovering||expanded)?1:0, transition:'opacity 0.15s', minWidth:28, minHeight:28, justifyContent:'center' }}
          >
            <ChevronIcon open={expanded} />
            {subtasks.length > 0 && !subtaskLoading && (
              <span style={{ fontSize: 10, color: expanded ? 'var(--accent)' : 'var(--text3)' }}>{progress.done}/{progress.total}</span>
            )}
            {subtaskLoading && <span style={{ fontSize: 9, color:'var(--text3)' }}>…</span>}
          </button>

          <button onClick={(e) => { e.stopPropagation(); onEdit(task); }} title="Edit"
            style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', padding:'4px 5px', borderRadius: 5, display:'flex', alignItems:'center', opacity: hovering?1:0, transition:'opacity 0.15s', minWidth:28, minHeight:28, justifyContent:'center' }}
            onMouseEnter={e => { e.currentTarget.style.color='var(--text)'; e.currentTarget.style.background='var(--border)'; }}
            onMouseLeave={e => { e.currentTarget.style.color='var(--text3)'; e.currentTarget.style.background='none'; }}
          ><EditIcon /></button>

          <button onClick={(e) => { e.stopPropagation(); handleDelete(); }} title="Delete"
            style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', padding:'4px 5px', borderRadius: 5, display:'flex', alignItems:'center', opacity: hovering?1:0, transition:'opacity 0.15s', minWidth:28, minHeight:28, justifyContent:'center' }}
            onMouseEnter={e => { e.currentTarget.style.color='#EF4444'; e.currentTarget.style.background='rgba(239,68,68,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.color='var(--text3)'; e.currentTarget.style.background='none'; }}
          ><TrashIcon /></button>
        </div>
      </div>

      {expanded && (
        <div style={{ padding:'0 12px 10px 40px', borderTop:'1px solid var(--divider)' }}>
          <SubtaskList subtasks={subtasks} onToggle={toggleSubtask} onUpdate={updateSubtask} onRemove={removeSubtask} onAdd={addSubtask}/>
        </div>
      )}
    </div>
  );
}